import type { SetNavigate } from '@repo/types/calendar';

import { useEventCalendarRangeDates } from '@repo/hooks/useEventCalendarRangeDates';
import { cn } from '@repo/lib/cn';
import { VIEW_KEY } from '@repo/types/enums/viewKey';
import { format } from 'date-fns';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';

import { Button, ButtonGroup } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuShortcut, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useCredentials } from '@/store/auth-store';

interface EventCalendarHeaderProps {
  date: Date;
  changeDate: (value: number) => void;
  onTodayClick: () => void;
  setNavigate: SetNavigate;
  view: VIEW_KEY;
  onEventCreateOpen: () => void;
}

export default function EventCalendarHeader(eventCalendarHeaderProps: EventCalendarHeaderProps) {
  const { date, changeDate, view, setNavigate, onEventCreateOpen, onTodayClick } = eventCalendarHeaderProps;
  const { lvl } = useCredentials();
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        document.documentElement.style.setProperty('--calendar-header-height', `${height}px`);
      }
    };
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, []);

  return (
    <div ref={headerRef} className="md:bg-background bg-background-dark sticky top-0 z-30 w-full p-4" data-desktop="true">
      <div className="hidden lg:flex lg:flex-row lg:justify-between lg:gap-2">
        <EventCalendarDatePicker date={date} view={view} setNavigate={setNavigate} />
        <EventCalendarNavigation changeDate={changeDate} setNavigate={setNavigate} onTodayClick={onTodayClick} />
        <EventCalendarViewDropdown setNavigate={setNavigate} view={view} />
        <EventCalendarAddButton onEventCreateOpen={onEventCreateOpen} />
      </div>

      <div className="grid grid-cols-2 grid-rows-2 place-items-end gap-2 lg:hidden">
        <div className="mr-auto">
          <EventCalendarDatePicker date={date} view={view} setNavigate={setNavigate} />
        </div>

        <EventCalendarAddButton onEventCreateOpen={onEventCreateOpen} />

        <div className={cn(lvl > 0 ? 'mr-auto' : 'ml-auto')}>
          <EventCalendarNavigation changeDate={changeDate} setNavigate={setNavigate} onTodayClick={onTodayClick} />
        </div>

        <div className={'col-2 row-2 ml-auto'}>
          <EventCalendarViewDropdown setNavigate={setNavigate} view={view} />
        </div>
      </div>
    </div>
  );
}

interface EventCalendarAddButtonProps {
  onEventCreateOpen: () => void;
}

function EventCalendarAddButton(eventCalendarAddButtonProps: EventCalendarAddButtonProps) {
  const { onEventCreateOpen } = eventCalendarAddButtonProps;
  const { lvl } = useCredentials();

  return (
    <Button onClick={onEventCreateOpen} variant={'primary'} className={cn(lvl <= 0 && 'hidden')}>
      <PlusIcon /> <span className="hidden md:block">Dodaj</span>
    </Button>
  );
}

interface EventCalendarViewDropdownProps {
  view: VIEW_KEY;
  setNavigate: SetNavigate;
}
function EventCalendarViewDropdown(eventCalendarViewDropdownProps: EventCalendarViewDropdownProps) {
  const { view, setNavigate } = eventCalendarViewDropdownProps;
  const setView = (newView: VIEW_KEY) => {
    setNavigate({ newViewKey: newView });
  };

  const names = {
    [VIEW_KEY.DAY]: 'Dzień',
    [VIEW_KEY.WEEK]: 'Tydzień',
    [VIEW_KEY.MONTH]: 'Miesiąc',
    [VIEW_KEY.AGENDA]: 'Agenda',
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          {names[view]}
          <ChevronDownIcon className="-me-1" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-32">
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => setView(VIEW_KEY.MONTH)}>
            {names[VIEW_KEY.MONTH]} <DropdownMenuShortcut>M</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setView(VIEW_KEY.WEEK)}>
            {names[VIEW_KEY.WEEK]} <DropdownMenuShortcut>W</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setView(VIEW_KEY.DAY)}>
            {names[VIEW_KEY.DAY]} <DropdownMenuShortcut>D</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setView(VIEW_KEY.AGENDA)}>
            {names[VIEW_KEY.AGENDA]} <DropdownMenuShortcut>A</DropdownMenuShortcut>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface EventCalendarHeaderNavProps {
  changeDate: (value: number) => void;
  setNavigate: SetNavigate;
  onTodayClick: () => void;
}
function EventCalendarNavigation(eventCalendarHeaderNavProps: EventCalendarHeaderNavProps) {
  const { changeDate, setNavigate, onTodayClick } = eventCalendarHeaderNavProps;
  return (
    <div className="ml-auto flex flex-row gap-2">
      {/* date navigation */}
      <ButtonGroup>
        {/* prev */}
        <Button variant="outline" onClick={() => changeDate(-1)}>
          <ChevronLeftIcon />
        </Button>
        {/* select date */}
        <Button
          variant="outline"
          onClick={() => {
            setNavigate({ newDate: new Date() });
            onTodayClick();
          }}
        >
          Dzisiaj
        </Button>
        <Button variant="outline" onClick={() => changeDate(1)}>
          <ChevronRightIcon />
        </Button>
      </ButtonGroup>
    </div>
  );
}

interface EventCalendarDatePickerProps {
  view: VIEW_KEY;
  date: Date;
  setNavigate: SetNavigate;
}

function EventCalendarDatePicker(eventCalendarDatePickerProps: EventCalendarDatePickerProps) {
  const { view, date, setNavigate } = eventCalendarDatePickerProps;
  const { after, before } = useEventCalendarRangeDates(date, view);

  const viewportDateRange = useMemo<string>(() => {
    const dateFormat = 'MMM dd, yyyy';

    if (view === VIEW_KEY.DAY) {
      return format(after, dateFormat);
    } else {
      return `${format(after, dateFormat)} - ${format(before, dateFormat)}`;
    }
  }, [view, after, before]);

  const onNavigate = (date?: Date) => {
    if (!date) return;
    setNavigate({ newDate: date });
  };

  return (
    <DatePicker selected={date} onSelect={onNavigate}>
      <Button variant={'ghost'} className={'h-fit min-h-fit w-fit px-1 py-0.5'}>
        <div className="mr-2 flex-col text-start">
          <div className="text-start text-lg font-semibold capitalize sm:text-xl">{format(date, 'LLLL yyyy')}</div>
          <div className="text-muted-foreground text-start text-xs sm:text-sm">{viewportDateRange}</div>
        </div>
        <ChevronDownIcon className="text-muted-foreground size-4" />
      </Button>
    </DatePicker>
  );
}
